import { useState } from "react";
import { useTranslation } from "react-i18next";

const FAQ_EN = [
  {
    category: "For Clients",
    items: [
      {
        q: "How does Profio work?",
        a: "Profio is a platform for online consultations. Connect with verified specialists — lawyers, psychologists, coaches, financial advisors, IT experts, and more. Browse profiles, read real reviews, and send a request directly to a specialist. The specialist sees your request and unlocks your contact to get in touch.",
      },
      {
        q: "Is it free for clients?",
        a: "Yes — completely free. Browsing, searching, sending requests, and contacting specialists all cost nothing. Clients never pay the platform. You pay the specialist directly for their services, at the rate they set themselves.",
      },
      {
        q: "How do I find the right specialist?",
        a: "Use the search page to filter by category, location, price range, and rating. Each profile shows the specialist's bio, services, reviews, and availability. You can send a request to multiple specialists at once.",
      },
      {
        q: "How do I contact a specialist?",
        a: "Open a specialist's profile and send a request — describe what you need and your preferred time. Once the specialist responds, you'll receive a notification and can continue the conversation directly.",
      },
      {
        q: "How do I pay for services?",
        a: "Payment is arranged directly between you and the specialist — Profio does not process or hold client payments. Once connected, you agree on a method and pay the specialist directly.",
      },
      {
        q: "Can I try a session before committing?",
        a: "Many specialists offer a trial or introductory consultation at a reduced rate. Look for the 'Trial available' label on their profile.",
      },
      {
        q: "Are specialists verified?",
        a: "Yes. Specialists go through an identity verification process via our Telegram bot before their profile becomes publicly visible. Verified specialists display a verification badge on their profile.",
      },
    ],
  },
  {
    category: "For Specialists",
    items: [
      {
        q: "How do I join as a specialist?",
        a: "Sign up, select the 'Specialist' role, complete your profile, and start the verification process via our Telegram bot. Once approved, your profile goes live and clients can find you.",
      },
      {
        q: "How does the coin system work?",
        a: "Each specialist has a coin wallet. When a client sends a request and you want to respond, you spend coins to unlock their contact details. New specialists receive a bonus on signup to get started. You can top up your balance at any time.",
      },
      {
        q: "How much does it cost to unlock a request?",
        a: "The coin cost is shown before you unlock each request. The amount depends on the service category. You can check your balance on the Wallet page at any time.",
      },
      {
        q: "Can I set my own rates?",
        a: "Absolutely. You set your own rate and trial price when creating or editing your profile. You can update them at any time.",
      },
      {
        q: "How do I get paid?",
        a: "Clients pay you directly — Profio does not hold or process payments on your behalf. You and the client agree on a payment method after connecting.",
      },
      {
        q: "What if a client cancels?",
        a: "Cancellation terms are agreed between you and the client. For disputes, contact our support team and we'll help mediate.",
      },
    ],
  },
  {
    category: "Account & Safety",
    items: [
      {
        q: "How do I delete my account?",
        a: "Contact us via the support form or send the /delete command through our Telegram bot. Your data will be removed within 60 days.",
      },
      {
        q: "Is my personal data safe?",
        a: "We take privacy seriously. Verification files are stored securely and never shared with third parties. See our Privacy Policy for full details.",
      },
      {
        q: "I forgot my password — what do I do?",
        a: "On the login page, click 'Forgot password'. You'll receive an email with a reset link. If it doesn't arrive, check your spam folder.",
      },
      {
        q: "How do I report a problem or an abusive user?",
        a: "Use the Contact page or reach out through the Telegram support bot. We review all reports and take action within 24 hours.",
      },
    ],
  },
];

const FAQ_UK = [
  {
    category: "Для клієнтів",
    items: [
      {
        q: "Як працює Profio?",
        a: "Profio — платформа для онлайн-консультацій. Зв'яжіться з верифікованими спеціалістами: юристами, психологами, коучами, фінансовими консультантами, IT-фахівцями та іншими. Переглядайте профілі, читайте справжні відгуки та надсилайте запит безпосередньо спеціалісту. Він бачить ваш запит та відкриває контакт, щоб зв'язатись із вами.",
      },
      {
        q: "Чи безкоштовно для клієнтів?",
        a: "Так — повністю безкоштовно. Пошук, перегляд профілів, надсилання запитів і зв'язок зі спеціалістами — усе безкоштовно. Клієнти нічого не платять платформі. Ви розраховуєтесь зі спеціалістом напряму за тарифом, який він сам встановлює.",
      },
      {
        q: "Як знайти потрібного спеціаліста?",
        a: "Скористайтесь пошуком: фільтруйте за категорією, містом, ціною та рейтингом. У кожному профілі є опис, перелік послуг, відгуки та доступність. Можна надіслати запит одразу кільком спеціалістам.",
      },
      {
        q: "Як зв'язатись зі спеціалістом?",
        a: "Відкрийте профіль спеціаліста й надішліть запит — опишіть, що потрібно, та зручний час. Як тільки спеціаліст відповість, ви отримаєте сповіщення і зможете продовжити спілкування напряму.",
      },
      {
        q: "Як відбувається оплата послуг?",
        a: "Оплата відбувається напряму між вами та спеціалістом — Profio не обробляє і не утримує платежі клієнтів. Після з'єднання ви домовляєтесь про зручний спосіб оплати і платите спеціалісту безпосередньо.",
      },
      {
        q: "Чи можна спробувати послугу перед тим, як визначитись?",
        a: "Багато спеціалістів пропонують пробну або вступну консультацію за зниженою ціною. Шукайте мітку «Є пробна» на їхньому профілі.",
      },
      {
        q: "Чи верифіковані спеціалісти?",
        a: "Так. Спеціалісти проходять верифікацію особи через наш Telegram-бот, перш ніж їхній профіль стає публічним. Верифіковані спеціалісти мають відповідний значок у профілі.",
      },
    ],
  },
  {
    category: "Для спеціалістів",
    items: [
      {
        q: "Як зареєструватись як спеціаліст?",
        a: "Зареєструйтесь, оберіть роль «Спеціаліст», заповніть профіль і пройдіть верифікацію через наш Telegram-бот. Після підтвердження ваш профіль стає активним, і клієнти можуть вас знайти.",
      },
      {
        q: "Як працює система монет?",
        a: "Кожен спеціаліст має гаманець з монетами. Коли клієнт надсилає запит, ви витрачаєте монети, щоб розблокувати його контактні дані та відповісти. Нові спеціалісти отримують бонусні монети при реєстрації. Баланс можна поповнити в будь-який момент.",
      },
      {
        q: "Скільки коштує розблокування запиту?",
        a: "Вартість у монетах відображається перед розблокуванням кожного запиту. Сума залежить від категорії послуги. Баланс гаманця можна перевірити на сторінці «Гаманець».",
      },
      {
        q: "Чи можу я встановити власну ціну?",
        a: "Звичайно. Ви самостійно встановлюєте свій тариф і ціну пробної консультації при створенні або редагуванні профілю. Їх можна змінити в будь-який момент.",
      },
      {
        q: "Як отримати оплату від клієнтів?",
        a: "Клієнти платять вам напряму — Profio не утримує й не обробляє платежі від вашого імені. Ви та клієнт домовляєтесь про зручний спосіб оплати після з'єднання.",
      },
      {
        q: "Що відбувається, якщо клієнт скасовує?",
        a: "Умови скасування узгоджуються між вами та клієнтом. У разі суперечок зверніться до нашої служби підтримки — ми допоможемо врегулювати ситуацію.",
      },
    ],
  },
  {
    category: "Акаунт і безпека",
    items: [
      {
        q: "Як видалити акаунт?",
        a: "Зверніться через форму підтримки або надішліть команду /delete через наш Telegram-бот. Ваші дані будуть видалені протягом 60 днів.",
      },
      {
        q: "Чи в безпеці мої особисті дані?",
        a: "Ми серйозно ставимось до конфіденційності. Верифікаційні файли зберігаються в захищеному середовищі та ніколи не передаються третім особам. Деталі — у нашій Політиці конфіденційності.",
      },
      {
        q: "Я забув(ла) пароль — що робити?",
        a: "На сторінці входу натисніть «Забули пароль». Ви отримаєте листа з посиланням для скидання. Якщо лист не надійшов — перевірте папку зі спамом.",
      },
      {
        q: "Як поскаржитись на порушення або агресивного користувача?",
        a: "Скористайтесь сторінкою «Контакти» або напишіть через Telegram-бот підтримки. Ми розглядаємо всі звернення та вживаємо заходів протягом 24 годин.",
      },
    ],
  },
];

function ChevronIcon({ open }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-4 w-4 shrink-0 text-white/50 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M5 8l5 5 5-5" />
    </svg>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 last:border-0">
      <button
        className="flex w-full items-start justify-between gap-4 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-white/90 leading-snug">{q}</span>
        <ChevronIcon open={open} />
      </button>
      {open && (
        <p className="pb-5 text-sm text-white/65 leading-relaxed">{a}</p>
      )}
    </div>
  );
}

export default function FAQ() {
  const { i18n } = useTranslation();
  const isUk = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("uk");

  const groups = isUk ? FAQ_UK : FAQ_EN;
  const title = isUk ? "Часті запитання" : "Frequently Asked Questions";
  const subtitle = isUk
    ? "Усе, що вам потрібно знати про Profio"
    : "Everything you need to know about Profio";

  return (
    <div className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 md:px-6 pt-16 pb-28 md:pb-16">
        <h1 className="font-unbounded text-3xl font-bold">{title}</h1>
        <p className="mt-3 text-white/60">{subtitle}</p>

        <div className="mt-12 space-y-10">
          {groups.map((group) => (
            <section key={group.category}>
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-[#7269e3]">
                {group.category}
              </h2>
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-5">
                {group.items.map((item) => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-[#7269e3]/10 ring-1 ring-[#7269e3]/30 p-6 text-center">
          <p className="text-sm text-white/70">
            {isUk ? "Не знайшли відповідь?" : "Didn't find your answer?"}
          </p>
          <a
            href="/contact"
            className="mt-3 inline-block rounded-xl bg-[#7269e3] px-6 py-2.5 text-sm font-semibold text-white hover:brightness-110 transition"
          >
            {isUk ? "Зв'яжіться з нами" : "Contact us"}
          </a>
        </div>
      </div>
    </div>
  );
}
